import { useAuth, useOrganization } from '@clerk/nextjs';
import { useMemo, useState } from 'react';
import { toast } from '@/code/frontend/hooks/use-toast';
import { fullName, userInitials } from '@/code/lib/utils/initials';
import { RouterOutput, trpc } from '@/code/lib/utils/trpc';

// --- Relative time helper ---

function relativeTime(date: Date | string): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 60) return 'just now';
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// --- Types ---

type TeamMemberData = RouterOutput['organizations']['getTeamMembers'][number];

// --- Inline form styles ---

const panelStyle = {
  marginTop: '12px',
  padding: '16px',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  backgroundColor: '#fafafa',
} as React.CSSProperties;

const panelHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '8px',
} as React.CSSProperties;

const panelTitle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  margin: 0,
} as React.CSSProperties;

const closeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '16px',
  color: '#9ca3af',
  padding: '0 4px',
  lineHeight: 1,
} as React.CSSProperties;

const panelDesc = {
  fontSize: '13px',
  color: '#6b7280',
  margin: '0 0 12px 0',
} as React.CSSProperties;

const formInputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'inherit',
  backgroundColor: '#fff',
  boxSizing: 'border-box' as const,
  outline: 'none',
} as React.CSSProperties;

const formLabelStyle = {
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  display: 'block',
  marginBottom: '4px',
} as React.CSSProperties;

const formFieldStyle = {
  marginBottom: '10px',
} as React.CSSProperties;

const submitBtnStyle = {
  fontSize: '13px',
  fontWeight: 500,
  color: '#fff',
  backgroundColor: '#7c3aed',
  padding: '8px 18px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
} as React.CSSProperties;

const submitBtnDisabledStyle = {
  ...submitBtnStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
} as React.CSSProperties;

// --- Add Member Form ---

function AddMemberPanel({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [seatType, setSeatType] = useState<'FULL_SEAT' | 'VIEWER'>('FULL_SEAT');
  const [role, setRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [note, setNote] = useState('');

  const submitMutation = trpc.organizations.submitSeatRequest.useMutation({
    onSuccess: () => {
      setFirstName('');
      setLastName('');
      setEmail('');
      setSeatType('FULL_SEAT');
      setRole('MEMBER');
      setNote('');
      onSuccess();
    },
    onError: () => {
      // Error is displayed inline via submitMutation.isError
    },
  });

  const canSubmit =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    email.trim() !== '' &&
    !submitMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    submitMutation.mutate({
      type: 'ADD',
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      seatType,
      role,
      note: note.trim() || undefined,
    });
  };

  return (
    <div style={panelStyle}>
      <div style={panelHeader}>
        <p style={panelTitle}>Request to Add a Member</p>
        <button type="button" onClick={onClose} style={closeBtn}>
          ×
        </button>
      </div>
      <p style={panelDesc}>
        Fill out the details below. Our team will review your request and set up
        the new seat.
      </p>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ ...formFieldStyle, flex: 1 }}>
            <label style={formLabelStyle}>First name *</label>
            <input
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={formInputStyle}
              placeholder="Jane"
            />
          </div>
          <div style={{ ...formFieldStyle, flex: 1 }}>
            <label style={formLabelStyle}>Last name *</label>
            <input
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={formInputStyle}
              placeholder="Doe"
            />
          </div>
        </div>
        <div style={formFieldStyle}>
          <label style={formLabelStyle}>Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={formInputStyle}
            placeholder="jane@company.com"
          />
        </div>
        <div style={formFieldStyle}>
          <label style={formLabelStyle}>Seat type</label>
          <select
            value={seatType}
            onChange={(e) =>
              setSeatType(e.target.value as 'FULL_SEAT' | 'VIEWER')
            }
            style={formInputStyle}
          >
            <option value="FULL_SEAT">Full Seat</option>
            <option value="VIEWER">Viewer</option>
          </select>
        </div>
        <div style={formFieldStyle}>
          <label style={formLabelStyle}>Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'ADMIN' | 'MEMBER')}
            style={formInputStyle}
          >
            <option value="ADMIN">Admin</option>
            <option value="MEMBER">Member</option>
          </select>
          <span
            style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '4px',
              display: 'block',
            }}
          >
            {role === 'ADMIN' ? 'Can manage team' : 'Standard access'}
          </span>
        </div>
        <div style={formFieldStyle}>
          <label style={formLabelStyle}>Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ ...formInputStyle, resize: 'vertical', minHeight: '48px' }}
            rows={2}
            placeholder="Any additional context..."
          />
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          style={canSubmit ? submitBtnStyle : submitBtnDisabledStyle}
        >
          {submitMutation.isPending ? 'Submitting...' : 'Submit Request'}
        </button>
        {submitMutation.isSuccess && (
          <p
            style={{
              fontSize: '13px',
              color: '#16a34a',
              marginTop: '8px',
            }}
          >
            Request submitted successfully
          </p>
        )}
        {submitMutation.isError && (
          <p
            style={{
              fontSize: '13px',
              color: '#dc2626',
              marginTop: '8px',
            }}
          >
            {submitMutation.error.message}
          </p>
        )}
      </form>
    </div>
  );
}

// --- Remove Member Form ---

function RemoveMemberPanel({
  members,
  onClose,
  onSuccess,
}: {
  members: TeamMemberData[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const selectedMember = members.find((m) => m.id === selectedId);

  const submitMutation = trpc.organizations.submitSeatRequest.useMutation({
    onSuccess: (_data, variables) => {
      toast({
        title: 'Request submitted',
        description: `Request to remove ${variables.firstName} ${variables.lastName} has been sent.`,
      });
      setSelectedId(null);
      setNote('');
      onSuccess();
    },
    onError: (err) => {
      toast({
        title: 'Failed to submit request',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const canSubmit = selectedMember != null && !submitMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selectedMember) return;
    submitMutation.mutate({
      type: 'REMOVE',
      firstName: selectedMember.firstName ?? '',
      lastName: selectedMember.lastName ?? '',
      email: selectedMember.primaryEmail ?? '',
      seatType: selectedMember.canOwnCampaigns ? 'FULL_SEAT' : 'VIEWER',
      note: note.trim() || undefined,
    });
  };

  return (
    <div style={panelStyle}>
      <div style={panelHeader}>
        <p style={panelTitle}>Request to Remove a Member</p>
        <button type="button" onClick={onClose} style={closeBtn}>
          ×
        </button>
      </div>
      <p style={panelDesc}>
        Select the team member you'd like to remove. Our team will review and
        process the change.
      </p>

      <form onSubmit={handleSubmit}>
        {/* Member picker */}
        <div style={formFieldStyle}>
          <label style={formLabelStyle}>Team member *</label>
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value || null)}
            style={formInputStyle}
          >
            <option value="">Select a team member...</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {fullName(m)} — {m.primaryEmail ?? 'no email'}
              </option>
            ))}
          </select>
        </div>

        {selectedMember && (
          <>
            <div style={formFieldStyle}>
              <label style={formLabelStyle}>Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{
                  ...formInputStyle,
                  resize: 'vertical',
                  minHeight: '48px',
                }}
                rows={2}
                placeholder="Reason for removal, how to handle campaigns..."
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              style={canSubmit ? submitBtnStyle : submitBtnDisabledStyle}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
            {submitMutation.isSuccess && (
              <p
                style={{
                  fontSize: '13px',
                  color: '#16a34a',
                  marginTop: '8px',
                }}
              >
                Request submitted successfully
              </p>
            )}
            {submitMutation.isError && (
              <p
                style={{
                  fontSize: '13px',
                  color: '#dc2626',
                  marginTop: '8px',
                }}
              >
                {submitMutation.error.message}
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
}

// --- Your Requests section ---

function SeatRequestsSection() {
  const utils = trpc.useUtils();
  const { data: requests = [], isLoading } =
    trpc.organizations.getSeatRequests.useQuery();
  const updateRequest = trpc.organizations.updateSeatRequest.useMutation({
    onSuccess: () => utils.organizations.getSeatRequests.invalidate(),
  });
  const deleteRequest = trpc.organizations.deleteSeatRequest.useMutation({
    onSuccess: () => utils.organizations.getSeatRequests.invalidate(),
  });

  if (isLoading) {
    return (
      <div
        style={{ ...requestsSectionStyle, color: '#9ca3af', fontSize: '13px' }}
      >
        Loading requests...
      </div>
    );
  }

  const pending = requests.filter((r) => r.resolvedAt == null);
  const resolved = requests.filter(
    (r) => r.resolvedAt != null && r.resolvedAction !== 'REJECTED',
  );

  if (pending.length === 0 && resolved.length === 0) return null;

  return (
    <div style={requestsSectionStyle}>
      <p
        style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#111827',
          margin: '0 0 8px 0',
        }}
      >
        Admin Requests
      </p>

      {pending.length > 0 && (
        <div style={{ marginBottom: resolved.length > 0 ? '12px' : '0' }}>
          {pending.map((r) => (
            <div key={r.id} style={requestRowStyle}>
              <span style={requestDotPending} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    {r.type === 'ADD' ? 'Add' : 'Remove'}{' '}
                    <strong>
                      {r.firstName} {r.lastName}
                    </strong>
                  </span>
                  {r.type === 'ADD' && (
                    <select
                      style={{
                        fontSize: '12px',
                        padding: '1px 4px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                      }}
                      value={r.seatType}
                      onChange={(e) =>
                        updateRequest.mutate({
                          requestId: r.id,
                          seatType: e.target.value as 'FULL_SEAT' | 'VIEWER',
                        })
                      }
                    >
                      <option value="FULL_SEAT">Full Seat</option>
                      <option value="VIEWER">Viewer</option>
                    </select>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {r.email}
                  {r.requestedBy &&
                    ` · by ${r.requestedBy.firstName} ${r.requestedBy.lastName}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteRequest.mutate({ requestId: r.id })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  fontSize: '14px',
                  padding: '2px 4px',
                }}
                title="Cancel request"
              >
                ×
              </button>
              <span style={requestTimeStyle}>{relativeTime(r.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <div>
          {resolved.map((r) => {
            const wasRejected = r.resolvedAction === 'REJECTED';
            const actionLabel = wasRejected
              ? `Rejected: ${r.type === 'ADD' ? 'Add' : 'Remove'}`
              : r.type === 'ADD'
                ? 'Added'
                : 'Removed';
            return (
              <div key={r.id} style={requestRowStyle}>
                <span
                  style={
                    wasRejected
                      ? {
                          ...requestDotResolved,
                          color: '#ef4444',
                          backgroundColor: '#fef2f2',
                        }
                      : requestDotResolved
                  }
                >
                  {wasRejected ? '✗' : '✓'}
                </span>
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                  {actionLabel} {r.firstName} {r.lastName} ({r.email})
                </span>
                <span style={requestTimeStyle}>
                  {relativeTime(r.resolvedAt as string | Date)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const requestsSectionStyle = {
  marginTop: '16px',
  padding: '14px 16px',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  backgroundColor: '#fafafa',
} as React.CSSProperties;

const requestRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 0',
} as React.CSSProperties;

const requestDotPending = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#f59e0b',
  flexShrink: 0,
} as React.CSSProperties;

const requestDotResolved = {
  fontSize: '12px',
  color: '#22c55e',
  flexShrink: 0,
  width: '14px',
  textAlign: 'center' as const,
} as React.CSSProperties;

const requestTimeStyle = {
  fontSize: '12px',
  color: '#9ca3af',
  marginLeft: 'auto',
  whiteSpace: 'nowrap' as const,
} as React.CSSProperties;

// --- Styles ---

const styles = {
  tabList: {
    display: 'flex',
    gap: '0',
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '16px',
  } as React.CSSProperties,
  tabActive: {
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid #000',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#000',
  } as React.CSSProperties,
  tabInactive: {
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#6b7280',
  } as React.CSSProperties,
  badge: {
    fontSize: '12px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    borderRadius: '999px',
    padding: '1px 8px',
    fontWeight: 400,
  } as React.CSSProperties,
  searchWrap: {
    position: 'relative' as const,
    marginBottom: '16px',
  } as React.CSSProperties,
  searchIcon: {
    position: 'absolute' as const,
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '14px',
    height: '14px',
    color: '#9ca3af',
  } as React.CSSProperties,
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  } as React.CSSProperties,
  tableWrap: {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
  } as React.CSSProperties,
  tableHeader: {
    display: 'grid',
    padding: '10px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
  headerCell: {
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: 500,
  } as React.CSSProperties,
  tableRow: {
    display: 'grid',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
  } as React.CSSProperties,
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#7c3aed',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0,
  } as React.CSSProperties,
  userName: {
    fontSize: '14px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  userEmail: {
    fontSize: '12px',
    color: '#9ca3af',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  seatBadge: (isCampaign: boolean) =>
    ({
      padding: '2px 10px',
      borderRadius: '6px',
      border: '1px solid',
      fontSize: '12px',
      fontWeight: 500,
      ...(isCampaign
        ? {
            color: '#1d4ed8',
            borderColor: '#bfdbfe',
            backgroundColor: '#eff6ff',
          }
        : {
            color: '#92400e',
            borderColor: '#fde68a',
            backgroundColor: '#fffbeb',
          }),
    }) as React.CSSProperties,
  adminBadge: {
    padding: '2px 8px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    fontSize: '11px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    marginLeft: '6px',
  } as React.CSSProperties,
  empty: {
    padding: '24px',
    textAlign: 'center' as const,
    color: '#9ca3af',
    fontSize: '14px',
  } as React.CSSProperties,
  footer: {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#9ca3af',
  } as React.CSSProperties,
  requestLink: {
    color: '#7c3aed',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    padding: 0,
  } as React.CSSProperties,
};

const gridCols4 = { gridTemplateColumns: '1fr 100px 100px 60px' };
const gridCols4Members = {
  gridTemplateColumns: 'minmax(0, 1fr) 90px 100px 90px',
};
// 5-column variant used when the org has teams enabled. Adds a "Team" column
// between Role and Seat Type, sized to fit a team name + small role badge.
const gridCols5Members = {
  gridTemplateColumns: 'minmax(0, 1fr) 90px 100px 140px 90px',
};

// --- Main component ---

export function TeamManagementPage() {
  const { invitations, memberships } = useOrganization({
    invitations: { infinite: true },
    memberships: { infinite: true },
  });
  const { orgRole, userId: currentClerkUserId } = useAuth();
  const { data: teamMembers = [], isLoading } =
    trpc.organizations.getTeamMembers.useQuery();
  // Surface team affiliation in the Members table when the org has teams
  // enabled. Read-only here — the Reps tab on the home dashboard is the
  // canonical surface for promote / demote / move / remove. Queries are
  // gated on `canCreateTeams` so we don't fire them for non-teams orgs.
  const org = trpc.organizations.getOrganization.useQuery();
  const orgHasTeams = org.data?.canCreateTeams === true;
  const teamsList = trpc.team.list.useQuery(undefined, {
    enabled: orgHasTeams,
  });
  const allMemberships = trpc.team.listAllMemberships.useQuery(undefined, {
    enabled: orgHasTeams,
  });
  const [activeTab, setActiveTab] = useState<'members' | 'invitations'>(
    'members',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showRequest, setShowRequest] = useState<'add' | 'remove' | null>(null);

  const utils = trpc.useUtils();

  const changeRoleMutation = trpc.organizations.changeUserRole.useMutation({
    onSuccess: () => {
      toast({ title: 'Role updated' });
      void utils.organizations.getTeamMembers.invalidate();
      void memberships?.revalidate?.();
    },
    onError: (err) => {
      toast({
        title: 'Failed to update role',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const isAdmin = orgRole === 'org:admin';
  const pendingInvitations = invitations?.data ?? [];
  const membershipList = memberships?.data ?? [];

  const roleByUserIdentifier = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membershipList) {
      if (m.publicUserData?.userId) {
        map.set(m.publicUserData.userId, m.role);
      }
      const email = m.publicUserData?.identifier.trim().toLowerCase();
      if (email) map.set(`email:${email}`, m.role);
    }
    return map;
  }, [membershipList]);

  // Build a map of internal userId → { teamName, role } so we can render the
  // Team column on the Members row without per-row lookups. `teamsList` is
  // small (org-scoped), so the join is cheap. Returns `null` for users with
  // no membership.
  const teamByUserId = useMemo(() => {
    const map = new Map<
      string,
      { teamId: string; teamName: string; role: 'ADMIN' | 'MEMBER' }
    >();
    if (!orgHasTeams || !teamsList.data || !allMemberships.data) return map;
    const teamNameById = new Map(teamsList.data.map((t) => [t.id, t.name]));
    for (const m of allMemberships.data) {
      const name = teamNameById.get(m.teamId);
      if (!name) continue;
      map.set(m.userId, { teamId: m.teamId, teamName: name, role: m.role });
    }
    return map;
  }, [orgHasTeams, teamsList.data, allMemberships.data]);

  const filteredMembers = useMemo(
    () =>
      teamMembers.filter((m) =>
        fullName(m).toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [teamMembers, searchQuery],
  );

  const handleRequestSuccess = () => {
    void utils.organizations.getSeatRequests.invalidate();
  };

  if (isLoading) {
    return <div style={styles.empty}>Loading...</div>;
  }

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Tabs */}
      <div style={styles.tabList}>
        <button
          type="button"
          style={
            activeTab === 'members' ? styles.tabActive : styles.tabInactive
          }
          onClick={() => setActiveTab('members')}
        >
          Members
          <span style={styles.badge}>{teamMembers.length}</span>
        </button>
        <button
          type="button"
          style={
            activeTab === 'invitations' ? styles.tabActive : styles.tabInactive
          }
          onClick={() => setActiveTab('invitations')}
        >
          Invitations
          <span style={styles.badge}>{pendingInvitations.length}</span>
        </button>
      </div>

      {activeTab === 'members' && (
        <>
          {/* Search */}
          <div style={styles.searchWrap}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
              style={styles.searchIcon}
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M5.4 2.2a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4ZM.6 5.4a4.8 4.8 0 1 1 8.7 2.8l3.9 3.8a.8.8 0 0 1-1.2 1.2L8.2 9.3A4.8 4.8 0 0 1 .6 5.4Z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {/* Members table */}
          <div style={styles.tableWrap}>
            <div
              style={{
                ...styles.tableHeader,
                ...(orgHasTeams ? gridCols5Members : gridCols4Members),
              }}
            >
              <span style={styles.headerCell}>User</span>
              <span style={styles.headerCell}>Joined</span>
              <span style={styles.headerCell}>Role</span>
              {orgHasTeams && <span style={styles.headerCell}>Team</span>}
              <span style={styles.headerCell}>Seat Type</span>
            </div>

            {filteredMembers.map((member) => {
              const clerkRole =
                roleByUserIdentifier.get(member.clerkUserId ?? '') ??
                roleByUserIdentifier.get(
                  `email:${member.primaryEmail?.trim().toLowerCase() ?? ''}`,
                );
              const isAdminUser = clerkRole === 'org:admin';
              const isSelf = member.clerkUserId === currentClerkUserId;
              const teamAffiliation = teamByUserId.get(member.id) ?? null;

              return (
                <div
                  key={member.id}
                  style={{
                    ...styles.tableRow,
                    ...(orgHasTeams ? gridCols5Members : gridCols4Members),
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      minWidth: 0,
                    }}
                  >
                    <div style={styles.avatar}>{userInitials(member)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.userName}>{fullName(member)}</div>
                      <div style={styles.userEmail}>
                        {member.primaryEmail ?? ''}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    {member.createdAt
                      ? new Date(member.createdAt).toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '--'}
                  </span>
                  <span>
                    {isAdmin && !isSelf ? (
                      <select
                        style={{
                          ...formInputStyle,
                          width: 'auto',
                          padding: '4px 8px',
                          fontSize: '12px',
                        }}
                        value={
                          clerkRole === 'org:admin' ? 'org:admin' : 'org:member'
                        }
                        disabled={changeRoleMutation.isPending}
                        onChange={(e) =>
                          changeRoleMutation.mutate({
                            userId: member.id,
                            role: e.target.value as 'org:admin' | 'org:member',
                          })
                        }
                      >
                        <option value="org:admin">Admin</option>
                        <option value="org:member">Member</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {isAdminUser ? 'Admin' : 'Member'}
                      </span>
                    )}
                  </span>
                  {orgHasTeams && (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        minWidth: 0,
                      }}
                    >
                      {teamAffiliation ? (
                        <>
                          <span
                            style={{
                              fontSize: '13px',
                              color: '#111827',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={teamAffiliation.teamName}
                          >
                            {teamAffiliation.teamName}
                          </span>
                          {teamAffiliation.role === 'ADMIN' && (
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: 600,
                                color: '#7c3aed',
                                background: '#ede9fe',
                                border: '1px solid #ddd6fe',
                                borderRadius: '4px',
                                padding: '1px 6px',
                                lineHeight: 1.4,
                                flexShrink: 0,
                              }}
                            >
                              Admin
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                          —
                        </span>
                      )}
                    </span>
                  )}
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {!member.canOwnCampaigns && (
                      <span style={styles.seatBadge(false)}>Viewer</span>
                    )}
                  </span>
                </div>
              );
            })}

            {filteredMembers.length === 0 && (
              <div style={styles.empty}>
                {searchQuery
                  ? 'No members match your search'
                  : 'No team members found'}
              </div>
            )}
          </div>

          {/* Footer -- request links */}
          {isAdmin && (
            <div style={styles.footer}>
              <button
                type="button"
                style={styles.requestLink}
                onClick={() =>
                  setShowRequest(showRequest === 'add' ? null : 'add')
                }
              >
                Request to add member
              </button>
              <span>·</span>
              <button
                type="button"
                style={styles.requestLink}
                onClick={() =>
                  setShowRequest(showRequest === 'remove' ? null : 'remove')
                }
              >
                Request to remove member
              </button>
            </div>
          )}

          {/* Inline request forms */}
          {showRequest === 'add' && (
            <AddMemberPanel
              onClose={() => setShowRequest(null)}
              onSuccess={handleRequestSuccess}
            />
          )}
          {showRequest === 'remove' && (
            <RemoveMemberPanel
              members={teamMembers}
              onClose={() => setShowRequest(null)}
              onSuccess={handleRequestSuccess}
            />
          )}

          {/* Your Requests (admin only) */}
          {isAdmin && <SeatRequestsSection />}
        </>
      )}

      {activeTab === 'invitations' && (
        <div style={styles.tableWrap}>
          <div style={{ ...styles.tableHeader, ...gridCols4 }}>
            <span style={styles.headerCell}>User</span>
            <span style={styles.headerCell}>Invited</span>
            <span style={styles.headerCell}>Role</span>
            <span style={styles.headerCell}>Status</span>
          </div>

          {pendingInvitations.map((inv) => {
            const firstLetter = inv.emailAddress.charAt(0).toUpperCase();
            return (
              <div key={inv.id} style={{ ...styles.tableRow, ...gridCols4 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      ...styles.avatar,
                      backgroundColor: '#d1d5db',
                      color: '#6b7280',
                      fontSize: '14px',
                    }}
                  >
                    {firstLetter}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={styles.userEmail}>{inv.emailAddress}</div>
                  </div>
                </div>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  {inv.createdAt
                    ? new Date(inv.createdAt).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : '--'}
                </span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  {inv.role === 'org:admin' ? 'Admin' : 'Member'}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    color: '#f59e0b',
                    fontWeight: 500,
                  }}
                >
                  {inv.status}
                </span>
              </div>
            );
          })}

          {pendingInvitations.length === 0 && (
            <div style={styles.empty}>No invitations to display</div>
          )}
        </div>
      )}
    </div>
  );
}
